import { useCallback, useEffect } from 'react';
import { Popper } from '@mui/material';
import { usePopover } from 'hooks/common/usePopover';

import { MenuProps } from '../../../../components/menus/types';
import { Toolbar } from '../../../../components/ui/Toolbar';
import { Icon } from '../../../../components/ui/Icon';

export const TableRowMenu = ({ editor, appendTo }: MenuProps) => {
  const { anchorEl, open, openPopover, closePopover } =
    usePopover<HTMLElement>();

  const shouldShow = useCallback(() => {
    const { state } = editor;
    const { selection } = state;
    const { $anchor } = selection;

    const isRowSelected = editor.isActive('tableRow');
    const isPointingInsideCell =
      $anchor.parent.type === editor.schema.nodes.tableCell;

    return isRowSelected || isPointingInsideCell;
  }, [editor]);

  const addRowBefore = useCallback(() => {
    editor.chain().focus().addRowBefore().run();
  }, [editor]);

  const addRowAfter = useCallback(() => {
    editor.chain().focus().addRowAfter().run();
  }, [editor]);

  const deleteRow = useCallback(() => {
    editor.chain().focus().deleteRow().run();
  }, [editor]);

  // Update menu position when table row is selected
  const updateMenuPosition = useCallback(() => {
    if (
      !editor.isActive ||
      !editor.view ||
      !shouldShow() ||
      !appendTo?.current
    ) {
      closePopover();
      return;
    }

    // Get the selected row
    const { state } = editor;
    const { selection } = state;
    const { $anchor } = selection;

    // Find the closest table cell
    let cellPos = -1;
    let depth = $anchor.depth;
    while (depth > 0) {
      const node = $anchor.node(depth);
      if (node.type === editor.schema.nodes.tableCell) {
        cellPos = $anchor.before(depth);
        break;
      }
      depth--;
    }

    if (cellPos === -1) return closePopover();

    // Position at the left of the row
    const domCell = editor.view.nodeDOM(cellPos) as HTMLElement;
    if (!domCell) return closePopover();

    const cells = document.querySelectorAll('.grip-row.selected');
    if (cells.length === 0) {
      closePopover();
      return;
    }

    const cell = cells[0];
    const rect = cell.getBoundingClientRect();
    openPopover(appendTo.current);

    // If needed, set position data attributes for custom positioning
    if (anchorEl) {
      const popoverElement = anchorEl as HTMLElement;
      popoverElement.dataset.x = `${rect.left - 10}`;
      popoverElement.dataset.y = `${rect.top + rect.height / 2}`;
    }
  }, [editor, shouldShow, openPopover, closePopover, appendTo, anchorEl]);

  // Set up event listeners
  useEffect(() => {
    const onSelectionChange = () => {
      if (shouldShow()) {
        updateMenuPosition();
      } else {
        closePopover();
      }
    };

    editor.on('selectionUpdate', onSelectionChange);
    editor.on('focus', onSelectionChange);

    // Run once on mount
    if (shouldShow()) {
      updateMenuPosition();
    }

    return () => {
      editor.off('selectionUpdate', onSelectionChange);
      editor.off('focus', onSelectionChange);
    };
  }, [editor, updateMenuPosition, shouldShow, closePopover]);

  return (
    <Popper
      open={open && shouldShow()}
      anchorEl={anchorEl}
      placement='left'
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 10],
          },
        },
        {
          name: 'flip',
          options: {
            fallbackPlacements: ['right', 'top', 'bottom'],
          },
        },
        {
          name: 'preventOverflow',
          options: {
            boundary: 'viewport',
            padding: 8,
          },
        },
      ]}
      style={{
        zIndex: 9999,
      }}
    >
      <Toolbar.Wrapper isVertical>
        <Toolbar.Button tooltip='Add row before' onClick={addRowBefore}>
          <Icon name='Plus' />
        </Toolbar.Button>
        <Toolbar.Button tooltip='Delete row' onClick={deleteRow}>
          <Icon name='Trash' />
        </Toolbar.Button>
        <Toolbar.Button tooltip='Add row after' onClick={addRowAfter}>
          <Icon name='Plus' />
        </Toolbar.Button>
      </Toolbar.Wrapper>
    </Popper>
  );
};

export default TableRowMenu;
